import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  Keyboard,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
} from "react-native";

type Options = {
  containerRef?: React.RefObject<any>;
  enabled?: boolean;
  blurActiveOnWeb?: boolean;
  /** Minimum drag distance to be considered a user scroll (px) */
  dragThreshold?: number; // default 12
  /** Safe time after focus to prevent immediate blur (ms) */
  focusGraceMs?: number; // default 300
};

function isInputLike(el: Element | null) {
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  return (
    tag === "input" ||
    tag === "textarea" ||
    (el as HTMLElement).isContentEditable === true
  );
}

export function useDismissKeyboardOnScroll({
  containerRef,
  enabled = true,
  blurActiveOnWeb = true,
  dragThreshold = 12,
  focusGraceMs = 300,
}: Options = {}) {
  const keyboardVisibleRef = useRef(false);

  // ---- iOS/native: track keyboard visibility
  useEffect(() => {
    if (!enabled) return;
    const showSub = Keyboard.addListener("keyboardDidShow", () => {
      keyboardVisibleRef.current = true;
    });
    const hideSub = Keyboard.addListener("keyboardDidHide", () => {
      keyboardVisibleRef.current = false;
    });
    return () => {
      showSub.remove();
      hideSub.remove();
    };
  }, [enabled]);

  // ---- Web state
  const lastFocusAtRef = useRef(0);
  const pointerDownAtRef = useRef<{ x: number; y: number } | null>(null);
  const pointerDownTargetRef = useRef<EventTarget | null>(null);
  const maxMoveRef = useRef(0);

  const dismissNow = useCallback(() => {
    if (!enabled) return;

    if (Platform.OS === "ios") {
      if (keyboardVisibleRef.current) Keyboard.dismiss();
      return;
    }

    if (Platform.OS === "web" && blurActiveOnWeb) {
      const active = document.activeElement as HTMLElement | null;
      if (isInputLike(active)) active?.blur();
    }
  }, [enabled, blurActiveOnWeb]);

  // ---- Web listeners with safeguards
  useEffect(() => {
    if (!enabled || Platform.OS !== "web") return;

    const el: HTMLElement | Window | null =
      (containerRef?.current as HTMLElement | null) ?? window;
    const target: any = el || window;

    const onFocus = (e: Event) => {
      if (isInputLike(e.target as Element)) {
        lastFocusAtRef.current = Date.now();
      }
    };

    const onPointerDown = (e: PointerEvent | TouchEvent | MouseEvent) => {
      pointerDownTargetRef.current = e.target as EventTarget | null;

      if ("clientX" in e && "clientY" in e) {
        pointerDownAtRef.current = {
          x: (e as PointerEvent).clientX,
          y: (e as PointerEvent).clientY,
        };
      } else if ("touches" in e && (e as TouchEvent).touches[0]) {
        const t = (e as TouchEvent).touches[0];
        pointerDownAtRef.current = { x: t.clientX, y: t.clientY };
      } else {
        pointerDownAtRef.current = null;
      }
      maxMoveRef.current = 0;
    };

    const onPointerMove = (e: PointerEvent | TouchEvent | MouseEvent) => {
      if (!pointerDownAtRef.current) return;
      let x: number | null = null;
      let y: number | null = null;

      if ("clientX" in e && "clientY" in e) {
        x = (e as PointerEvent).clientX;
        y = (e as PointerEvent).clientY;
      } else if ("touches" in e && (e as TouchEvent).touches[0]) {
        const t = (e as TouchEvent).touches[0];
        x = t.clientX;
        y = t.clientY;
      }

      if (x == null || y == null) return;

      const dx = Math.abs(x - pointerDownAtRef.current.x);
      const dy = Math.abs(y - pointerDownAtRef.current.y);
      const d = Math.max(dx, dy);
      if (d > maxMoveRef.current) maxMoveRef.current = d;
    };

    const onScroll = () => {
      // Only dismiss if:
      // 1) There is an active input-like element (indicating keyboard is open), and
      // 2) The grace period after focus has passed, and
      // 3) The user has dragged more than dragThreshold, and
      // 4) Pointer down did not start inside the input itself (so focusing can still open the keyboard)
      const now = Date.now();
      const active = document.activeElement as HTMLElement | null;
      const isActiveInput = isInputLike(active);

      if (!isActiveInput) return;
      if (now - lastFocusAtRef.current < focusGraceMs) return;
      if (maxMoveRef.current < dragThreshold) return;

      const downTarget = pointerDownTargetRef.current as Element | null;
      if (
        downTarget &&
        (downTarget === active || (active && active.contains(downTarget)))
      ) {
        // Gesture started on the currently focused input → do not blur.
        return;
      }

      dismissNow();
    };

    // Attach
    window.addEventListener("focus", onFocus, true);
    target.addEventListener("pointerdown", onPointerDown, { passive: true });
    target.addEventListener("pointermove", onPointerMove, { passive: true });
    target.addEventListener("touchstart", onPointerDown as any, {
      passive: true,
    });
    target.addEventListener("touchmove", onPointerMove as any, {
      passive: true,
    });
    target.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      window.removeEventListener("focus", onFocus, true);
      target.removeEventListener("pointerdown", onPointerDown as any);
      target.removeEventListener("pointermove", onPointerMove as any);
      target.removeEventListener("touchstart", onPointerDown as any);
      target.removeEventListener("touchmove", onPointerMove as any);
      target.removeEventListener("scroll", onScroll as any);
    };
  }, [enabled, containerRef, dragThreshold, focusGraceMs, dismissNow]);

  // ---- RN handlers (iOS)
  const onScrollBeginDrag = useCallback(
    (_e?: NativeSyntheticEvent<NativeScrollEvent>) => {
      if (Platform.OS === "ios") dismissNow();
    },
    [dismissNow]
  );

  return useMemo(
    () => ({
      onScrollBeginDrag,
      dismissNow,
    }),
    [onScrollBeginDrag, dismissNow]
  );
}
